import LoadingSpinner from './LoadingSpinner';
import React, { useState } from 'react';
import SyntheticCard from './SyntheticCard';
import { Plus, Search, AlertCircle, Activity } from 'lucide-react';

const logoGreen = '#95DE1A';
const redColor = '#FD0E0B';
const grayColor = '#6B7280';
const blueColor = '#0b7dc3';

function SyntheticDashboard({ 
  tests = [],               
  loading = false,          
  error: propError = null,  
  onAddClick, 
  onEdit, 
  onDelete, 
  onRun, 
  onViewResults, 
  onToggleActive,           
  userLimits,
  filterStatus = 'all',      
  onFilterChange,
  canAddSynthetic,
  darkMode = false
}) {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredTests = tests.filter(test => {
    const matchesSearch = !searchTerm || 
      test.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      test.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      test.status?.toLowerCase().includes(searchTerm.toLowerCase());

    if (!matchesSearch) return false;

    if (filterStatus === 'all') return true;
    if (filterStatus === 'success') return test.isActive && test.results?.[0]?.status === 'success';
    if (filterStatus === 'failed') return test.isActive && test.results?.[0]?.status === 'failed';
    if (filterStatus === 'inactive') return !test.isActive;
    return true;
  });

  const activeTests = filteredTests.filter(t => t.isActive);
  const inactiveTests = filteredTests.filter(t => !t.isActive);

  if (loading) {
    return <LoadingSpinner darkMode={darkMode} />;
  }

  const disabled = typeof canAddSynthetic !== 'function' || !canAddSynthetic();

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div className="relative flex-1 w-full">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            aria-label="Search synthetic tests"
            placeholder="Search tests..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`w-full pl-10 pr-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#95DE1A] focus:border-transparent ${
              darkMode 
                ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-400' 
                : 'bg-white border border-gray-200 text-gray-900'
            }`}
          />
        </div>
        <button
          onClick={onAddClick}
          disabled={disabled}
          className="px-6 py-3 text-white rounded-xl font-semibold flex items-center space-x-2 hover:shadow-lg transition disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          style={{ backgroundColor: logoGreen }}
          title={
            disabled
              ? 'You cannot add more tests due to your plan limits or expired subscription.'
              : `Maximum ${userLimits?.maxSyntheticTests} tests allowed`
          }
        >
          <Plus className="h-5 w-5" />
          <span>New Test</span>
        </button>
      </div>

      {propError && (
        <div className={`mb-6 p-4 rounded-xl flex items-center space-x-3 ${
          darkMode ? 'bg-red-900/30 border border-red-800' : 'bg-red-50 border border-red-200'
        }`}>
          <AlertCircle className="h-5 w-5 text-red-500" />
          <span className={`text-sm ${darkMode ? 'text-red-300' : 'text-red-700'}`}>{propError}</span>
        </div>
      )}

      {tests.length === 0 ? (
        <div className={`text-center py-20 rounded-3xl ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
          <div className="mb-4">
            <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center"
                 style={{ backgroundColor: `${logoGreen}20` }}>
              <Activity className="h-10 w-10" style={{ color: logoGreen }} />
            </div>
          </div>
          <h3 className={`text-2xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>No Synthetic Tests</h3>
          <p className={`mb-6 max-w-md mx-auto ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Create your first synthetic transaction test to monitor critical user flows.
          </p>
          <button
            onClick={onAddClick}
            disabled={disabled}
            className="px-6 py-3 text-white rounded-xl font-semibold"
            style={{ backgroundColor: logoGreen }}
          >
            Create Test
          </button>
        </div>
      ) : (
        <>
          {filteredTests.length === 0 && (
            <div
              className={`rounded-2xl border px-6 py-10 text-center ${
                darkMode ? 'border-gray-700 bg-gray-800 text-gray-200' : 'border-gray-200 bg-white text-gray-700'
              }`}
              role="status"
            >
              <Search className="mx-auto mb-3 h-8 w-8 text-gray-400" aria-hidden="true" />
              <h3 className="text-lg font-semibold">No tests match the current filters</h3>
              <button
                type="button"
                onClick={() => {
                  setSearchTerm('');
                  onFilterChange?.('all');
                }}
                className="mt-4 rounded-lg px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              >
                Clear filters
              </button>
            </div>
          )}

          {activeTests.length > 0 && (
            <div className="mb-10">
              <h2 className={`text-xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Active Tests</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {activeTests.map(test => (
                  <SyntheticCard
                    key={test.id}
                    test={test}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onRun={onRun}
                    onViewResults={onViewResults}
                    onToggleActive={onToggleActive}
                    darkMode={darkMode}
                  />
                ))}
              </div>
            </div>
          )}

          {inactiveTests.length > 0 && (
            <div>
              <h3 className={`text-lg font-semibold mb-4 flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Inactive Tests
                <span 
                  className="ml-2 px-2 py-1 text-xs rounded-full"
                  style={{ backgroundColor: `${redColor}20`, color: redColor }}
                >
                  Plan limited or manually paused
                </span>
              </h3>
              <div 
                className="rounded-2xl p-4 mb-6"
                style={{ backgroundColor: `${redColor}20`, border: `1px solid ${redColor}60` }}
              >
                <p className="text-sm" style={{ color: redColor }}>
                  <strong>Note:</strong> Inactive tests may be paused manually or by your plan limits. You can reactivate an individual test when your plan allows it.
                </p>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {inactiveTests.map(test => (
                  <SyntheticCard
                    key={test.id}
                    test={test}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onRun={onRun}
                    onViewResults={onViewResults}
                    onToggleActive={onToggleActive}
                    darkMode={darkMode}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default SyntheticDashboard;
